import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  searchStocksRatings,
  SearchStoriesParams,
} from "@/shared/api/firehose";
import stocksRatingsSSEClient from "@/shared/api/firehose/stocksRatingsSSEClient";
import { NewsFeedItem } from "@/shared/api/firehose/models";
import {
  firehoseFilterMatcher,
  firehosePortfolioMatcher,
} from "../../firehose-all/utils";

export const useSearchStocksRatings = () => {
  const [searchParams, setSearchParams] = useState<SearchStoriesParams>({});
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [page, setPage] = useState(1);
  const [allResults, setAllResults] = useState<NewsFeedItem[]>([]);
  const [hasMore, setHasMore] = useState(true);

  // AbortController to cancel previous requests
  const abortControllerRef = useRef<AbortController | null>(null);

  const queryClient = useQueryClient();

  const {
    data: _searchResults = [],
    error,
    isError,
    isLoading,
    isSuccess,
    refetch,
  } = useQuery({
    queryKey: ["searchStocksRatings", searchParams],
    queryFn: async ({ signal }) => {
      const results = await searchStocksRatings({ ...searchParams, signal });
      return results;
    },
    enabled: isSearchActive && Object.keys(searchParams).length > 0,
    staleTime: 0,
    gcTime: 5 * 60 * 1000, // 5 minutes
    // A search is an explicit, user-triggered action. Don't retry (a 429 must
    // not become 4 requests) and don't silently re-fire on window focus /
    // reconnect — the SSE subscription below keeps results live.
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Accumulate results when new page is loaded
  useEffect(() => {
    if (_searchResults && _searchResults.length >= 0 && isSearchActive) {
      const currentOffset = searchParams.offset ?? 0;
      const initialPage = searchParams.initialPage ?? 1;
      const initialOffset = (initialPage - 1) * (searchParams.limit ?? 50);

      if (currentOffset === initialOffset) {
        // Initial page for this search - replace results
        setAllResults(_searchResults);
        setHasMore(_searchResults.length > 0);
      } else {
        // Subsequent pages - append results
        setAllResults((prev) => [...prev, ..._searchResults]);
        // If we got an empty list, there are no more results
        setHasMore(_searchResults.length > 0);
      }
    }
  }, [
    _searchResults,
    isSearchActive,
    searchParams.offset,
    searchParams.limit,
    searchParams.initialPage,
  ]);

  const searchResults = isSearchActive ? allResults : [];

  const searchStocksRatingsData = useCallback(
    async (params: SearchStoriesParams) => {
      // Cancel previous request if it exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new AbortController for this request
      const newController = new AbortController();
      abortControllerRef.current = newController;

      const startPage = params.initialPage ?? 1;
      const limit = params.limit ?? 50;
      const initialOffset = (startPage - 1) * limit;

      const searchPayload: SearchStoriesParams = {
        ...params,
        offset: initialOffset,
        limit,
        initialPage: startPage,
      };

      setSearchParams(searchPayload);
      setIsSearchActive(true);
      setPage(startPage);
      setAllResults([]);
      setHasMore(true);
    },
    []
  );

  const loadNextPage = useCallback(() => {
    const newPage = page + 1;
    const newOffset = (newPage - 1) * (searchParams.limit ?? 50);

    setSearchParams((prev) => ({
      ...prev,
      offset: newOffset,
    }));
    setPage(newPage);
  }, [page, searchParams.limit]);

  const clearSearchResults = useCallback(() => {
    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Clear the React Query cache for search results
    queryClient.removeQueries({ queryKey: ["searchStocksRatings"] });

    // Clear search results and reset state
    setSearchParams({});
    setIsSearchActive(false);
    setPage(1);
    setAllResults([]);
    setHasMore(true);
    abortControllerRef.current = null;
  }, [queryClient]);

  // Filter SSE results to only include stories matching search params
  useEffect(() => {
    if (!isSearchActive) return;

    const unsubscribe = stocksRatingsSSEClient.subscribe(
      "message",
      (data: NewsFeedItem) => {
        let matched = false;

        if (searchParams.advanceFilter) {
          matched = firehoseFilterMatcher(data, searchParams.advanceFilter);
        } else if (searchParams.portfolios && searchParams.portfolios.length > 0) {
          matched = firehosePortfolioMatcher(data, searchParams.portfolios);
        } else if (searchParams.q) {
          // Simple text search — check if query appears in title or body
          const query = searchParams.q.toLowerCase();
          const titleMatch = data.title?.toLowerCase().includes(query);
          const bodyMatch = data.body?.toLowerCase().includes(query) ?? false;
          matched = titleMatch || bodyMatch;
        }

        if (matched) {
          // If it matches, add it to the search results
          queryClient.setQueryData<NewsFeedItem[]>(
            ["searchStocksRatings", searchParams],
            (oldData = []) => {
              return [data, ...oldData];
            }
          );
        }
      }
    );

    return unsubscribe;
  }, [isSearchActive, queryClient, searchParams]);

  return {
    searchResults,
    error,
    isError,
    isLoading,
    isSuccess,
    searchStocksRatingsData,
    clearSearchResults,
    refetch,
    loadNextPage,
    page,
    hasMore,
  };
};
